/**
 * Editable v2 annotation document with undo/redo and dirty tracking.
 *
 * Replaces `useAnnotationState`, and carries over the one behaviour that hook
 * got right and must not be lost (commit 8403d4164): the photo screen seeds
 * itself from navigation params — a snapshot of whatever the gallery held —
 * and reconciles against the server a render later. If the hook only read its
 * initial value once, a freshly-saved annotation looked lost on re-open.
 *
 * So a late-arriving document is adopted, guarded two ways: content-compared,
 * so the parent rebuilding the photo object is a no-op; and abandoned
 * permanently once the rep has drawn anything, so a slow response can never
 * delete live work.
 *
 * There is a second late arrival this hook has to handle that the old one did
 * not. v1 annotations cannot be converted without the photo's aspect ratio,
 * which is measured asynchronously. Until it resolves the migrator falls back
 * to an identity mapping, so the document is re-derived when the measurement
 * lands — same guards apply.
 *
 * ## Live edits vs. history
 *
 * Dragging an annotation emits a new position every frame. Those must NOT each
 * become an undo step, or the 20-deep history fills with one gesture and undo
 * rewinds by single frames instead of by actions. `updateItemLive` moves the
 * item without touching history; `commitHistory` takes one snapshot when the
 * gesture ends. Callers that change something in a single discrete action
 * (add, delete, restyle) use the combined helpers and get a snapshot for free.
 */

// Structural hook signatures avoid resolving React from the other platform's
// node_modules (mobile CI/EAS need not install the web application's packages).
interface AnnotationHooks {
  useState<T>(
    initial: T | (() => T),
  ): [T, (action: T | ((previous: T) => T)) => void];
  useRef<T>(initial: T): { current: T };
  useMemo<T>(factory: () => T, dependencies: readonly unknown[]): T;
  useCallback<T extends (...args: never[]) => unknown>(
    callback: T,
    dependencies: readonly unknown[],
  ): T;
}
import {
  docFingerprint,
  readAnnotations,
  serializeDoc,
} from "./annotationMigrate";
import type { AnnotationDoc, AnnotationItem } from "./annotationSchema";
import { SCHEMA_VERSION } from "./annotationSchema";

/** Matches the old canvas. Deep enough to undo a mistake, shallow enough that
 *  a long session cannot grow without bound. */
const HISTORY_LIMIT = 20;

export interface UseAnnotationDocParams {
  /** Raw `photo.annotations` — v1 record or v2 document. */
  initial: unknown;
  /** Null until measured; drives the v1 conversion. */
  imageAspect: number | null;
  screenWidth: number;
}

export interface UseAnnotationDocResult {
  doc: AnnotationDoc;
  items: AnnotationItem[];
  selectedId: string | null;
  select: (id: string | null) => void;
  /** Add and snapshot. */
  addItem: (item: AnnotationItem) => void;
  /** Replace an item WITHOUT a history snapshot — for per-frame drag updates. */
  updateItemLive: (item: AnnotationItem) => void;
  /** Snapshot whatever is on screen now. Call once when a drag ends. */
  commitHistory: () => void;
  /** Replace an item and snapshot, for one-shot edits (restyle, retype). */
  commitItem: (item: AnnotationItem) => void;
  removeItem: (id: string) => void;
  clearAll: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
  /** Call after a successful save so the current state becomes the baseline. */
  markSaved: () => void;
}

/** Supply each platform's own React instance; the shared core has no runtime
 * dependency on either application's React installation. */
export function createUseAnnotationDoc({
  useCallback,
  useMemo,
  useRef,
  useState,
}: AnnotationHooks) {
  return function useAnnotationDoc({
    initial,
    imageAspect,
    screenWidth,
  }: UseAnnotationDocParams): UseAnnotationDocResult {
    const baseline = useMemo(
      () => readAnnotations(initial, { screenWidth, imageAspect }),
      [initial, screenWidth, imageAspect],
    );
    const baselinePrint = useMemo(() => docFingerprint(baseline), [baseline]);

    const [items, setItems] = useState<AnnotationItem[]>(baseline.items);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [history, setHistory] = useState<AnnotationItem[][]>([
      baseline.items,
    ]);
    const [historyIndex, setHistoryIndex] = useState(0);

    // Tracked in state, not a ref. Adjusting state during render is the React
    // pattern for deriving from changing inputs, but it has to compare against
    // *state* — a ref mutated mid-render is not safe under StrictMode's double
    // invocation or a re-entrant render.
    const [appliedPrint, setAppliedPrint] = useState(baselinePrint);
    const [savedPrint, setSavedPrint] = useState(baselinePrint);

    const hasLocalEditsRef = useRef(false);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    if (!hasLocalEditsRef.current && baselinePrint !== appliedPrint) {
      setAppliedPrint(baselinePrint);
      setSavedPrint(baselinePrint);
      setItems(baseline.items);
      setHistory([baseline.items]);
      setHistoryIndex(0);
    }

    // Mirror history and its cursor so pushHistory can read them without being
    // rebuilt on every change (which would invalidate every callback below).
    const historyRef = useRef(history);
    historyRef.current = history;
    const historyIndexRef = useRef(historyIndex);
    historyIndexRef.current = historyIndex;

    const pushHistory = useCallback((next: AnnotationItem[]) => {
      // Computed outside the setState updaters: React can invoke an updater more
      // than once, so it must stay pure — no ref writes, no sibling setState.
      const truncated = historyRef.current.slice(
        0,
        historyIndexRef.current + 1,
      );
      // Drop any redo branch, then cap. Slicing from the end keeps the most
      // recent states, so the cap forgets the oldest rather than refusing new.
      const capped = [...truncated, next].slice(-HISTORY_LIMIT);
      historyRef.current = capped;
      historyIndexRef.current = capped.length - 1;
      setHistory(capped);
      setHistoryIndex(capped.length - 1);
    }, []);

    const setLive = useCallback((next: AnnotationItem[]) => {
      hasLocalEditsRef.current = true;
      setItems(next);
    }, []);

    const updateItemLive = useCallback(
      (item: AnnotationItem) => {
        setLive(itemsRef.current.map((e) => (e.id === item.id ? item : e)));
      },
      [setLive],
    );

    const commitHistory = useCallback(() => {
      pushHistory(itemsRef.current);
    }, [pushHistory]);

    const addItem = useCallback(
      (item: AnnotationItem) => {
        const next = [...itemsRef.current, item];
        setLive(next);
        pushHistory(next);
        setSelectedId(item.id);
      },
      [pushHistory, setLive],
    );

    const commitItem = useCallback(
      (item: AnnotationItem) => {
        const stamped = { ...item, updatedAt: new Date().toISOString() };
        const next = itemsRef.current.map((e) =>
          e.id === item.id ? stamped : e,
        );
        setLive(next);
        pushHistory(next);
      },
      [pushHistory, setLive],
    );

    const removeItem = useCallback(
      (id: string) => {
        const next = itemsRef.current.filter((item) => item.id !== id);
        setLive(next);
        pushHistory(next);
        setSelectedId((prev) => (prev === id ? null : prev));
      },
      [pushHistory, setLive],
    );

    const clearAll = useCallback(() => {
      setLive([]);
      pushHistory([]);
      setSelectedId(null);
    }, [pushHistory, setLive]);

    const undo = useCallback(() => {
      const next = historyIndexRef.current - 1;
      if (next < 0) return;
      hasLocalEditsRef.current = true;
      historyIndexRef.current = next;
      setHistoryIndex(next);
      setItems(history[next]);
      setSelectedId(null);
    }, [history]);

    const redo = useCallback(() => {
      const next = historyIndexRef.current + 1;
      if (next > history.length - 1) return;
      historyIndexRef.current = next;
      setHistoryIndex(next);
      setItems(history[next]);
      setSelectedId(null);
    }, [history]);

    const doc = useMemo<AnnotationDoc>(
      () => serializeDoc({ schema: SCHEMA_VERSION, items }),
      [items],
    );
    const currentPrint = useMemo(() => JSON.stringify(doc), [doc]);

    // State, not a ref: marking saved has to re-render or the Save button stays
    // enabled after a successful save.
    const markSaved = useCallback(() => {
      setSavedPrint(
        JSON.stringify(
          serializeDoc({ schema: SCHEMA_VERSION, items: itemsRef.current }),
        ),
      );
    }, []);

    return {
      doc,
      items,
      selectedId,
      select: setSelectedId,
      addItem,
      updateItemLive,
      commitHistory,
      commitItem,
      removeItem,
      clearAll,
      undo,
      redo,
      canUndo: historyIndex > 0,
      canRedo: historyIndex < history.length - 1,
      isDirty: currentPrint !== savedPrint,
      markSaved,
    };
  };
}
